import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    Button,
    Collapse,
    Badge,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    MarkEmailRead as MarkReadIcon,
    Mail as MailIcon,
    Send as SendIcon,
    Inbox as InboxIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import marketplaceService from '../services/marketplaceService';

const MessageItem = ({ msg, onMarkRead, onDelete, showMarkRead }) => {
    const [expanded, setExpanded] = useState(false);
    const isUnread = !msg.is_read && showMarkRead;

    return (
        <>
            <ListItem
                alignItems="flex-start"
                sx={{
                    bgcolor: isUnread ? 'action.hover' : 'transparent',
                    borderRadius: 2,
                    mb: 0.5,
                    cursor: 'pointer',
                }}
                onClick={() => {
                    setExpanded(!expanded);
                    if (isUnread) onMarkRead(msg.id);
                }}
            >
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isUnread && (
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                            )}
                            <Typography variant="body2" fontWeight={isUnread ? 700 : 400} noWrap sx={{ flexGrow: 1 }}>
                                {msg.message?.slice(0, 80)}{msg.message?.length > 80 ? '…' : ''}
                            </Typography>
                            {msg.listing_id && (
                                <Chip label={`Listing #${msg.listing_id}`} size="small" variant="outlined" />
                            )}
                        </Box>
                    }
                    secondary={
                        <Typography variant="caption" color="text.secondary">
                            {showMarkRead
                                ? `From: User #${msg.sender_id}`
                                : `To: User #${msg.receiver_id}`}
                            {' · '}
                            {msg.created_at
                                ? new Date(msg.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                : ''}
                        </Typography>
                    }
                />
                <ListItemSecondaryAction sx={{ display: 'flex', gap: 0.5 }}>
                    {isUnread && (
                        <IconButton
                            size="small"
                            title="Mark as read"
                            onClick={(e) => { e.stopPropagation(); onMarkRead(msg.id); }}
                        >
                            <MarkReadIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        size="small"
                        color="error"
                        title="Delete message"
                        onClick={(e) => { e.stopPropagation(); onDelete(msg.id); }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
                        {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                    </IconButton>
                </ListItemSecondaryAction>
            </ListItem>

            {/* Expanded message body */}
            <Collapse in={expanded} unmountOnExit>
                <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderRadius: 2, mx: 2, mb: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.message}
                    </Typography>
                </Box>
            </Collapse>
            <Divider component="li" />
        </>
    );
};

const MessagesPage = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [tab, setTab] = useState(0); // 0 = Inbox, 1 = Sent

    // Inbox
    const { data: inbox = [], isLoading: inboxLoading, error: inboxError } = useQuery({
        queryKey: ['inbox'],
        queryFn: () => marketplaceService.getInbox(),
    });

    // Sent
    const { data: sent = [], isLoading: sentLoading, error: sentError } = useQuery({
        queryKey: ['sentMessages'],
        queryFn: () => marketplaceService.getSentMessages(),
    });

    // Mark as read
    const markReadMutation = useMutation({
        mutationFn: (id) => marketplaceService.markMessageRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['inbox']);
        },
    });

    // Delete
    const deleteMutation = useMutation({
        mutationFn: (id) => marketplaceService.deleteMessage(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['inbox']);
            queryClient.invalidateQueries(['sentMessages']);
        },
    });

    const isLoading = tab === 0 ? inboxLoading : sentLoading;
    const error = tab === 0 ? inboxError : sentError;
    const messages = tab === 0 ? inbox : sent;
    const unreadCount = inbox.filter(m => !m.is_read).length;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={600} gutterBottom>Messages</Typography>
                <Typography variant="body1" color="text.secondary">
                    Your conversations with buyers and sellers
                </Typography>
            </Box>

            <Paper sx={{ borderRadius: 3 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    <Tab
                        icon={
                            <Badge badgeContent={unreadCount} color="error">
                                <InboxIcon />
                            </Badge>
                        }
                        label="Inbox"
                        iconPosition="start"
                    />
                    <Tab icon={<SendIcon />} label="Sent" iconPosition="start" />
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            Failed to load messages. Please try again.
                        </Alert>
                    )}

                    {!isLoading && !error && messages.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <MailIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                {tab === 0 ? 'Your inbox is empty' : 'No sent messages'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {tab === 0
                                    ? 'Messages from sellers and buyers will appear here.'
                                    : 'Messages you send to sellers will appear here.'}
                            </Typography>
                            <Button variant="contained" onClick={() => navigate('/parts')} sx={{ borderRadius: 3 }}>
                                Browse Parts
                            </Button>
                        </Box>
                    )}

                    {!isLoading && !error && messages.length > 0 && (
                        <List disablePadding>
                            {messages.map((msg) => (
                                <MessageItem
                                    key={msg.id}
                                    msg={msg}
                                    showMarkRead={tab === 0}
                                    onMarkRead={(id) => markReadMutation.mutate(id)}
                                    onDelete={(id) => deleteMutation.mutate(id)}
                                />
                            ))}
                        </List>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default MessagesPage;
